"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { ArrowRight, Shield, Globe, Lock, Zap, Server, Eye, EyeOff, Wifi, Star, CheckCircle } from "lucide-react";
import Image from "next/image";
import { ButtonLink } from "@/components/ui/button-link";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield,
  shield: Shield,
  Globe,
  globe: Globe,
  Lock,
  lock: Lock,
  Zap,
  zap: Zap,
  Server,
  server: Server,
  Eye,
  eye: Eye,
  EyeOff,
  "eye-off": EyeOff,
  Wifi,
  wifi: Wifi,
  Star,
  star: Star,
  CheckCircle,
  "check-circle": CheckCircle,
};

interface JourneyFeaturesGridProps {
  badge: {
    icon: string;
    text: string;
  };
  title: string;
  description: string;
  mainFeature: {
    title: {
      normal: string;
      highlight: string;
    };
    description: string;
    ctaText: string;
    ctaHref?: string;
    image: string;
  };
  gridFeatures: Array<{
    title?: string;
    subtitle?: string;
    image: string;
    backgroundColor: "secondary" | "muted";
  }>;
  stats: Array<{
    value: string;
    label: string;
    icon?: string;
    backgroundColor: "white" | "dark";
  }>;
}

const JourneyFeaturesGrid = ({
  badge,
  title,
  description,
  mainFeature,
  gridFeatures,
  stats,
}: JourneyFeaturesGridProps) => {
  const BadgeIcon = badge?.icon ? (ICON_MAP[badge.icon] || Shield) : Shield;
  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-5xl mx-auto flex flex-col items-center gap-16">
        
        {/* Header Section */}
        <div className="flex flex-col items-center text-center gap-6 max-w-3xl">
          <BlurFade delay={0.1}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-foreground text-[10px] font-bold tracking-widest uppercase">
              {BadgeIcon && <BadgeIcon size={12} fill="currentColor" />}
              {badge?.text}
            </div>
          </BlurFade>

          <BlurFade delay={0.2}>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              {title}
            </h2>
          </BlurFade>

          <BlurFade delay={0.3}>
            <p className="text-lg text-foreground/60 font-medium leading-relaxed">
              {description}
            </p>
          </BlurFade>
        </div>

        {/* Joined Grid Container */}
        <BlurFade delay={0.4} className={`w-full ${mainFeature?.image ? 'mt-48' : 'mt-12'} relative`}>
          <div className="w-full relative z-10">
            {/* Top Row: Joined Featured Card */}
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="bg-foreground p-12 lg:p-16 flex flex-col justify-center gap-8 border-b lg:border-b-0 lg:border-r border-white/10 rounded-none lg:rounded-tl-[3rem] order-2 lg:order-1">
                <h3 className="text-2xl lg:text-[1.75rem] font-black tracking-tighter text-white leading-tight">
                  {mainFeature.title.normal} <br />
                  <span className="text-secondary italic font-black">
                    {mainFeature.title.highlight}
                  </span>
                </h3>
                <p className="text-base text-white/60 font-medium max-w-md">
                  {mainFeature.description}
                </p>
                <ButtonLink
                  href={mainFeature.ctaHref}
                  variant="text"
                  size="text"
                  icon={<ArrowRight size={14} />}
                  className="mt-4 self-start"
                >
                  {mainFeature.ctaText}
                </ButtonLink>
              </div>

              <div className={`relative bg-foreground/80 ${mainFeature.image ? 'min-h-[450px]' : 'min-h-[200px]'} rounded-t-[3rem] lg:rounded-t-none lg:rounded-tr-[3rem] order-1 lg:order-2`}>
                {/* The "Poke Out" Image - Using clip-path to ensure ONLY top overflow */}
                {mainFeature.image && (
                  <div
                    className="absolute inset-0 pointer-events-none z-20"
                    style={{ clipPath: "inset(-100% 0px 0px 0px)" }}
                  >
                    <Image
                      src={mainFeature.image}
                      alt="Tiera User"
                      fill
                      className="object-contain object-bottom scale-[2.0] origin-bottom translate-y-2"
                      priority
                    />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 to-transparent pointer-events-none rounded-tr-[3rem]" />
              </div>
            </div>

            {/* Bottom Row: Joined Three Columns */}
            <div className="grid grid-cols-1 md:grid-cols-12">
              {/* Grid Features (Dynamic) */}
              {gridFeatures.map((feature, index) => {
                const bgClass = feature.backgroundColor === "secondary" ? "bg-secondary" : "bg-muted";
                const colSpan = index === 0 ? "md:col-span-6 lg:col-span-5" : "md:col-span-6 lg:col-span-4";
                const roundedClass = index === 0 ? "lg:rounded-bl-[3rem]" : "";
                const borderClass = index === 0
                  ? "border-b md:border-b-0 md:border-r border-foreground/5"
                  : "border-b md:border-b-0 lg:border-r border-foreground/5";

                return (
                  <div
                    key={index}
                    className={`${colSpan} ${bgClass} p-12 flex flex-col ${feature.title ? "gap-12" : "justify-between gap-12"} ${feature.image ? 'min-h-[450px]' : 'min-h-[200px]'} ${borderClass} rounded-none ${roundedClass}`}
                  >
                    {feature.title && (
                      <h4 className="text-xl lg:text-[1.5rem] font-black tracking-tighter text-foreground leading-tight order-2 md:order-1">
                        {feature.title}
                      </h4>
                    )}
                    {feature.image && (
                    <div className={`relative flex-1 flex items-center justify-center ${index === 0 ? "px-4 order-1 md:order-2" : "-mx-12"}`}>
                      {index === 1 ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <Image
                            src={feature.image}
                            alt="Feature illustration"
                            width={600}
                            height={450}
                            className="w-full h-auto object-contain"
                            unoptimized
                            style={{
                              maskImage: "radial-gradient(ellipse at center, rgba(0,0,0,1) 30%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 85%)",
                              WebkitMaskImage: "radial-gradient(ellipse at center, rgba(0,0,0,1) 30%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 85%)",
                            }}
                          />
                        </div>
                      ) : (
                        <Image
                          src={feature.image}
                          alt="Feature illustration"
                          width={400}
                          height={300}
                          className="w-full h-auto object-contain"
                          unoptimized
                        />
                      )}
                    </div>
                    )}
                    {feature.subtitle && (
                      <p className="text-sm font-bold text-foreground/60 leading-relaxed tracking-widest text-center">
                        {feature.subtitle}
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Stats (Dynamic) */}
              <div className="md:col-span-12 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 rounded-none">
                {stats.map((stat, index) => {
                  const StatIcon = stat.icon ? ICON_MAP[stat.icon as keyof typeof ICON_MAP] : null;
                  const bgClass = stat.backgroundColor === "white" ? "bg-white" : "bg-foreground";
                  const textClass = stat.backgroundColor === "white" ? "text-foreground" : "text-white";
                  const labelClass = stat.backgroundColor === "white" ? "text-muted-foreground" : "text-white/40";
                  const borderClass = index === 0 ? "border-b md:border-b-0 lg:border-b border-zinc-100" : "";
                  const roundedClass = index === 1 ? "rounded-b-[3rem] md:rounded-b-none md:rounded-br-[3rem] lg:rounded-br-[3rem]" : "";

                  return (
                    <div
                      key={index}
                      className={`${bgClass} p-12 flex flex-col items-center justify-center text-center gap-2 ${borderClass} ${roundedClass}`}
                    >
                      <div className={`text-5xl font-black tracking-tighter ${textClass}`}>
                        {stat.value}
                      </div>
                      <div className={`text-[10px] font-black tracking-widest ${labelClass} uppercase`}>
                        {stat.label}
                      </div>
                      {StatIcon && (
                        <div className="relative w-20 h-20 mt-4 text-secondary opacity-30">
                          <StatIcon size={80} strokeWidth={0.5} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Global Container Border Overlay for Sharpness */}
            <div className="absolute inset-0 rounded-[3rem] border border-zinc-100 pointer-events-none shadow-2xl shadow-zinc-200/50" />
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default JourneyFeaturesGrid;

